import { Inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, flatMap, switchMap, tap } from 'rxjs/operators';
import * as fromShared from '../../shared';
import {
    DisplayMessageMinimum,
    IStatusMessage,
    ResponseStatus
} from '../../shared';
import { LoggingService } from '../../shared/services/logging/logging.service';
import { IIndirectStructure } from '../IIndirectStructure';
import { IndirectJobsService } from '../indirect-jobs.service';
import * as fromIJ from './actions';

@Injectable()
export class IndirectJobsEffects {
    newIJ: Array<IIndirectStructure> = [];

    constructor(
        private actions$: Actions,
        @Inject(IndirectJobsService) private service: IndirectJobsService,
        @Inject(LoggingService) private log: LoggingService
    ) {}

    LoadIndirectJobs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromIJ.LoadIndirectJobs>(
                fromIJ.ActionTypes.LoadIndirectJobs
            ),
            tap(action =>
                this.log.debug({
                    type: 'IndirectJobsEffects:LoadIndirectJobs$:filtered',
                    action
                })
            ),
            switchMap(x =>
                this.service
                    .getIndirectJobs()
                    // .pipe(cs => JSON.parse(cs))
                    .pipe(
                        flatMap(is => [
                            new fromIJ.SetIndirectJobs(is),
                            new fromShared.Errors.RemoveErrors(
                                is
                                    .filter(
                                        i =>
                                            i.Status.StatusCode ===
                                            ResponseStatus.Success
                                    )
                                    .map(i => i.QB_ListID.toString())
                            ),
                            new fromShared.Errors.SetErrors(
                                [
                                    ...this.flattenNestedData(is)
                                        .filter(
                                            a =>
                                                a.Status.StatusCode >=
                                                DisplayMessageMinimum
                                        )
                                        .map(a => a.Status)
                                ].filter(fromShared.Errors.filterErrors)
                            )
                        ]),
                        catchError(fromShared.Errors.handleServiceError)
                    )
            )
        )
    );

    UpdateIndirectJobs$ = createEffect(() =>
        this.actions$.pipe(
            ofType<fromIJ.UpdateIndirectJobs>(
                fromIJ.ActionTypes.UpdateIndirectJobs
            ),
            tap(x =>
                this.log.verbose({
                    type: 'IndirectJobsEffects:UpdateIndirectJobs$',
                    x
                })
            ),
            switchMap(a =>
                this.service.putIndirectJobs([...a.payload]).pipe(
                    flatMap(resp => [
                        new fromIJ.UpdateIndirectJobsSuccess(resp),
                        new fromShared.Errors.RemoveErrors(
                            resp.Items.filter(
                                i =>
                                    i.Status.StatusCode ===
                                    ResponseStatus.Success
                            ).map(i => i.QB_ListID.toString())
                        ),
                        new fromShared.Errors.SetErrors(
                            [
                                ...resp.ErrorMessages.map(
                                    x =>
                                        ({
                                            StatusCode: -1,
                                            Message: x,
                                            MsgProcessID: 0,
                                            MsgEntityID: ''
                                        } as IStatusMessage)
                                ),
                                ...this.flattenNestedData(resp.Items)
                                    .filter(
                                        a =>
                                            a.Status.StatusCode >=
                                            DisplayMessageMinimum
                                    )
                                    .map(a => a.Status)
                            ].filter(fromShared.Errors.filterErrors)
                        )
                    ]),
                    catchError(fromShared.Errors.handleServiceError)
                )
            )
        )
    );

    public flattenNestedData(
        cs: Array<IIndirectStructure>
    ): Array<IIndirectStructure> {
        const flatten = cs => {
            return cs.reduce((r, { _children, ...rest }) => {
                r.push(rest);
                if (_children) {
                    r.push(...flatten(_children));
                }
                return r;
            }, []);
        };
        // this.log.verbose(flatten(cs));
        const flatcs = flatten(cs);
        return flatcs;
    }
}
